<template>
  <!-- Edit Cruise Dialog -->
  <TransitionRoot appear :show="dialogs.editCruise" as="template">
    <Dialog @close="closeDialog('editCruise')" class="relative z-50">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <DialogOverlay class="fixed inset-0 bg-black/25" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
              <EditCruiseForm
                v-if="selectedItems.cruiseForEdit"
                :cruise="selectedItems.cruiseForEdit"
                @updated="$emit('cruise-updated')"
                @close="closeDialog('editCruise')"
              />
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>

  <!-- Add Duck Dialog -->
  <TransitionRoot appear :show="dialogs.addDuck" as="template">
    <Dialog @close="closeDialog('addDuck')" class="relative z-50">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <DialogOverlay class="fixed inset-0 bg-black/25" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
              <AddDuckForm
                v-if="selectedItems.cruiseId"
                :cruise-id="selectedItems.cruiseId"
                :cruise-info="getCruiseInfo(selectedItems.cruiseId)"
                :warning-message="warningMessage"
                @created="$emit('duck-created')"
                @close="closeDialog('addDuck')"
              />
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>

  <!-- Duck Details Dialog -->
  <TransitionRoot appear :show="dialogs.duckDetails" as="template">
    <Dialog @close="closeDialog('duckDetails')" class="relative z-50">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <DialogOverlay class="fixed inset-0 bg-black/25" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
              <DuckDetailsDialog
                v-if="selectedItems.duck"
                :duck="selectedItems.duck"
                @close="closeDialog('duckDetails')"
                @updated="$emit('duck-updated')"
                @refresh-cruises="$emit('refresh-cruises')"
              />
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>

  <!-- Reset Confirmation Dialog -->
  <TransitionRoot appear :show="dialogs.resetConfirm" as="template">
    <Dialog @close="closeDialog('resetConfirm')" class="relative z-50">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <DialogOverlay class="fixed inset-0 bg-black/25" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel class="w-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
              <DialogTitle as="h3" class="text-lg font-medium leading-6 text-gray-900 mb-4">
                Reset All Ducks
              </DialogTitle>
              <div class="mb-4">
                <p class="text-sm text-gray-500 mb-2">Are you sure you want to reset all ducks for this cruise?</p>
                <p class="text-xs text-red-600">This action cannot be undone.</p>
              </div>

              <div class="flex justify-end space-x-3">
                <button
                  type="button"
                  class="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  @click="closeDialog('resetConfirm')"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  class="inline-flex justify-center rounded-md border border-transparent bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2 disabled:opacity-50"
                  @click="$emit('reset-confirmed')"
                  :disabled="loadingStates.resetting"
                >
                  <svg
                    v-if="loadingStates.resetting"
                    class="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                    <path class="opacity-75" fill="currentColor" d="m4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Reset Ducks
                </button>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>

  <!-- Cruise Messages Dialog -->
  <TransitionRoot appear :show="dialogs.cruiseMessages" as="template">
    <Dialog @close="closeDialog('cruiseMessages')" class="relative z-50">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <DialogOverlay class="fixed inset-0 bg-black/25" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-0 sm:p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel class="w-full sm:max-w-2xl transform overflow-hidden sm:rounded-2xl bg-white text-left align-middle shadow-xl transition-all sm:my-8 mobile-dialog-fullscreen">
              <div class="h-[85vh] sm:h-[70vh] max-h-[90vh] sm:max-h-[70vh] flex flex-col">
                <MessageList
                  v-if="selectedItems.cruiseId && cruiseName"
                  :cruise-id="selectedItems.cruiseId"
                  :title="`Messages for ${cruiseName}`"
                  :hide-header="false"
                  @update="$emit('messages-updated', $event)"
                  @close="closeDialog('cruiseMessages')"
                />
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>

  <!-- Duck Messages Dialog -->
  <TransitionRoot appear :show="dialogs.duckMessages" as="template">
    <Dialog @close="closeDialog('duckMessages')" class="relative z-50">
      <TransitionChild
        as="template"
        enter="duration-300 ease-out"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="duration-200 ease-in"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <DialogOverlay class="fixed inset-0 bg-black/25" />
      </TransitionChild>

      <div class="fixed inset-0 overflow-y-auto">
        <div class="flex min-h-full items-center justify-center p-0 sm:p-4 text-center">
          <TransitionChild
            as="template"
            enter="duration-300 ease-out"
            enter-from="opacity-0 scale-95"
            enter-to="opacity-100 scale-100"
            leave="duration-200 ease-in"
            leave-from="opacity-100 scale-100"
            leave-to="opacity-0 scale-95"
          >
            <DialogPanel class="w-full sm:max-w-2xl transform overflow-hidden sm:rounded-2xl bg-white text-left align-middle shadow-xl transition-all sm:my-8 mobile-dialog-fullscreen">
              <div class="h-[85vh] sm:h-[70vh] max-h-[90vh] sm:max-h-[70vh] flex flex-col">
                <MessageList
                  v-if="selectedItems.duckUuid && duckName"
                  :duck-uuid="selectedItems.duckUuid"
                  :title="`Messages for ${duckName}`"
                  :hide-header="false"
                  @update="$emit('messages-updated', $event)"
                  @close="closeDialog('duckMessages')"
                />
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>  
</template>

<script setup lang="ts">
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  DialogOverlay,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';
import EditCruiseForm from '@/components/demoadmin/EditCruiseForm.vue';
import AddDuckForm from '@/components/demoadmin/AddDuckForm.vue';
import DuckDetailsDialog from '@/components/demoadmin/DuckDetailsDialog.vue';
import MessageList from '@/components/demoadmin/MessageList.vue';
import type { DetailedCruise, DetailedDuck } from '@/interfaces/cruise';

interface DialogsState {
  editCruise: boolean;
  addDuck: boolean;
  duckDetails: boolean;
  resetConfirm: boolean;
  cruiseMessages: boolean;
  duckMessages: boolean;
}

interface LoadingStates {
  resetting: boolean;
  deleting?: boolean;
}

interface SelectedItems {
  cruiseId: number | null;
  cruiseForEdit: DetailedCruise | null;
  duck: DetailedDuck | null;
  duckUuid: string | null;
  cruiseData?: DetailedCruise | null;
}

const props = defineProps({
  dialogs: {
    type: Object as () => DialogsState,
    required: true
  },
  loadingStates: {
    type: Object as () => LoadingStates,
    required: true
  },
  selectedItems: {
    type: Object as () => SelectedItems,
    required: true
  },
  cruiseName: {
    type: String,
    default: ''
  },
  duckName: {
    type: String,
    default: ''
  },
  warningMessage: {
    type: String,
    default: ''
  }
});

const emit = defineEmits([
  'update:dialogs',
  'cruise-updated',
  'duck-created',
  'duck-updated',
  'reset-confirmed',
  'messages-updated',
  'refresh-cruises'
]);

const closeDialog = (dialogKey: keyof DialogsState) => {
  const updatedDialogs = { ...props.dialogs };
  updatedDialogs[dialogKey] = false;
  
  // Emit the updated dialogs object to parent
  // This allows the parent to track which dialog was closed
  emit('update:dialogs', updatedDialogs);
};

// Add a helper method to get cruise info for the AddDuckForm
const getCruiseInfo = (cruiseId: number | null) => {
  if (!cruiseId) return undefined;
  
  const cruise = props.selectedItems.cruiseData || 
    (props.selectedItems.cruiseForEdit?.id === cruiseId ? props.selectedItems.cruiseForEdit : null);
    
  // If we don't have the cruise in our props, try to find it in the parent component data
  if (!cruise) {
    // The form will fall back to default behavior without cruise info
    return undefined;
  }
  
  return {
    ducksCreated: cruise.ducksCreated || cruise.ducks?.length || 0,
    maxDucksAllowed: cruise.maxDucksAllowed || 0
  };
};
</script>

<style scoped>
/* Mobile responsive */
@media (max-width: 640px) {
  /* Full screen dialog for mobile */
  .mobile-dialog-fullscreen {
    margin: 0;
    height: 100vh;
    height: 100dvh;
    max-height: 100vh;
    max-height: 100dvh;
    width: 100%;
    border-radius: 0;
    overflow: hidden;
  }
}
</style>